import { Tooltip } from '@heroui/react';
import { ChevronRight, Plus } from 'lucide-react';
import { FC } from 'react';
import styled from 'styled-components';

import { Spinner } from '../Spinner';

// Guzik-CTA z liczbą + etykietą — niebieski dla kategorii, zielony dla produktów.
const CountCta = styled.button<{ $tone?: 'blue' | 'green' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  border: 1px solid ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? 'rgba(34, 197, 94, 0.45)' : '#86efac';
    return theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.45)' : '#93c5fd';
  }};
  background: ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? 'rgba(34, 197, 94, 0.14)' : '#dcfce7';
    return theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.14)' : '#dbeafe';
  }};
  color: ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? '#86efac' : '#15803d';
    return theme.mode === 'dark' ? '#93c5fd' : '#1d4ed8';
  }};

  &:hover {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.10);
    background: ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? 'rgba(34, 197, 94, 0.24)' : '#bbf7d0';
    return theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.24)' : '#bfdbfe';
  }};
  }

  &:active { transform: translateY(0.5px); box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06); }

  & > .cta-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 5px;
    font-size: 11px;
    font-weight: 400;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.10)' : 'rgba(255, 255, 255, 0.75)')};
  }

  & > .cta-chevron { width: 14px; height: 14px; opacity: 0.85; }
`;

// Guzik „Dodaj…" gdy brak pozycji (count === 0) — przerywana ramka, ten sam ton co CountCta.
const AddCta = styled.button<{ $tone?: 'blue' | 'green' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  border: 1px dashed ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? 'rgba(34, 197, 94, 0.55)' : '#86efac';
    return theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.55)' : '#93c5fd';
  }};
  color: ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? '#86efac' : '#15803d';
    return theme.mode === 'dark' ? '#93c5fd' : '#1d4ed8';
  }};

  &:hover {
    background: ${({ $tone, theme }) => {
    if ($tone === 'green') return theme.mode === 'dark' ? 'rgba(34, 197, 94, 0.14)' : '#dcfce7';
    return theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.14)' : '#dbeafe';
  }};
  }

  & > .cta-plus { width: 14px; height: 14px; }
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LoaderWrap = styled.span`
  display: inline-flex;
  align-items: center;
`;

interface OutletCountCellProps {
  // Liczba pozycji — null oznacza błąd/brak danych (nie pokazujemy guzika).
  count: number | null;
  // Trwa pobieranie licznika → spinner zamiast treści.
  isLoading: boolean;
  tone: 'blue' | 'green';
  // Etykieta na guziku liczby (np. „Kategorie", „Produkty").
  label: string;
  // Etykieta guzika dodawania gdy count === 0 (np. „Dodaj kategorię").
  addLabel: string;
  // Tooltip guzika nawigacji do listy.
  goTooltip: string;
  // Nawigacja do listy (count > 0).
  onOpen: () => void;
  // Nawigacja do dodawania (count === 0).
  onAdd: () => void;
}

export const OutletCountCell: FC<OutletCountCellProps> = ({
  count,
  isLoading,
  tone,
  label,
  addLabel,
  goTooltip,
  onOpen,
  onAdd,
}) => {
  if (isLoading) {
    return (
      <LoaderWrap>
        <Spinner size="sm" />
      </LoaderWrap>
    );
  }

  if (count === null) {
    return <Muted>—</Muted>;
  }

  if (count === 0) {
    return (
      <AddCta
        type="button"
        $tone={tone}
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        aria-label={addLabel}
      >
        <Plus className="cta-plus" />
        <span>{addLabel}</span>
      </AddCta>
    );
  }

  return (
    <Tooltip
      content={goTooltip}
      showArrow
      radius="sm"
      size="sm"
      delay={500}
      classNames={{
        base: 'pointer-events-auto',
        content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
      }}
    >
      <span>
        <CountCta
          type="button"
          $tone={tone}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          aria-label={label}
        >
          <span className="cta-count">{count}</span>
          <span>{label}</span>
          <ChevronRight className="cta-chevron" />
        </CountCta>
      </span>
    </Tooltip>
  );
};
